import React, { useEffect, useRef } from 'react';

const ORBIT_PERIOD_MS = 52000;
const ATTRACT_RADIUS = 105;
const MAX_PULL = 26;
const HOVER_SCALE = 1.12;

const STATIC_POSITIONS = [
  'left-1/2 top-0 -translate-x-1/2',
  'right-0 top-[28%]',
  'bottom-[8%] right-[4%]',
  'bottom-[5%] left-[5%]',
  'left-0 top-[31%]'
];

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function AbilityOrbitVisual({ abilities, count, countLabel, ariaLabel }) {
  const reduced = prefersReducedMotion();
  const containerRef = useRef(null);
  const boxRefs = useRef([]);
  const abilitiesRef = useRef(abilities);
  const mouseRef = useRef({ inBounds: false, x: 0, y: 0 });
  const orbitRef = useRef(0);
  const pullRef = useRef(abilities.map(() => ({ x: 0, y: 0, vx: 0, vy: 0 })));
  const scaleRef = useRef(abilities.map(() => 1));
  const hoveredRef = useRef(-1);
  const radiusRef = useRef(140);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);

  abilitiesRef.current = abilities;

  useEffect(() => {
    if (reduced) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    const measure = () => {
      const rect = container.getBoundingClientRect();
      radiusRef.current = Math.min(rect.width, rect.height) * 0.36;
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const pad = 48;
      mouseRef.current.inBounds =
        e.clientX >= rect.left - pad &&
        e.clientX <= rect.right + pad &&
        e.clientY >= rect.top - pad &&
        e.clientY <= rect.bottom + pad;
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    const onLeave = () => {
      mouseRef.current.inBounds = false;
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    container.addEventListener('mouseleave', onLeave);

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const deltaMs = Math.min(48, ts - lastTsRef.current);
      lastTsRef.current = ts;
      const dt = deltaMs / 16.67;

      orbitRef.current += ((Math.PI * 2) / ORBIT_PERIOD_MS) * deltaMs;

      const rect = container.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const radius = radiusRef.current;
      const mouse = mouseRef.current;
      const itemCount = abilitiesRef.current.length;

      for (let index = 0; index < itemCount; index += 1) {
        const el = boxRefs.current[index];
        if (!el) continue;

        const baseAngle = orbitRef.current + (index * Math.PI * 2) / itemCount - Math.PI / 2;
        const orbitX = Math.cos(baseAngle) * radius;
        const orbitY = Math.sin(baseAngle) * radius;
        const boxCenterX = cx + orbitX;
        const boxCenterY = cy + orbitY;

        let targetX = 0;
        let targetY = 0;
        if (mouse.inBounds) {
          const dx = mouse.x - boxCenterX;
          const dy = mouse.y - boxCenterY;
          const dist = Math.hypot(dx, dy);
          if (dist < ATTRACT_RADIUS && dist > 1) {
            const proximity = 1 - dist / ATTRACT_RADIUS;
            const pull = proximity ** 2;
            targetX = (dx / dist) * pull * MAX_PULL;
            targetY = (dy / dist) * pull * MAX_PULL;
          }
        }

        const body = pullRef.current[index];
        const stiffness = mouse.inBounds ? 0.17 : 0.11;
        const damping = 0.74;
        body.vx = (body.vx + (targetX - body.x) * stiffness * dt) * damping;
        body.vy = (body.vy + (targetY - body.y) * stiffness * dt) * damping;
        body.x += body.vx * dt;
        body.y += body.vy * dt;

        const x = orbitX + body.x;
        const y = orbitY + body.y;
        const targetScale = hoveredRef.current === index ? HOVER_SCALE : 1;
        const nextScale = scaleRef.current[index] + (targetScale - scaleRef.current[index]) * 0.2;
        scaleRef.current[index] = nextScale;

        el.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${nextScale})`;
        el.style.zIndex = hoveredRef.current === index ? 20 : 10;
      }

      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      observer.disconnect();
      window.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
      window.cancelAnimationFrame(rafRef.current);
      lastTsRef.current = 0;
    };
  }, [reduced]);

  return (
    <div
      ref={containerRef}
      className="relative mx-auto min-h-[370px] w-full max-w-md"
      aria-label={ariaLabel}
    >
      <div
        className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full border border-edge"
        aria-hidden
      />
      <div
        className="absolute left-1/2 top-1/2 h-60 w-60 -translate-x-1/2 -translate-y-1/2 rounded-full border border-edge-strong/70"
        aria-hidden
      />
      <div className="absolute left-1/2 top-1/2 flex h-44 w-44 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-center text-primary-foreground shadow-2xl shadow-black/20">
        <div>
          <strong className="block text-5xl leading-none">{count}</strong>
          <span className="mt-2 block text-xs font-semibold uppercase tracking-wider text-primary-foreground/85">
            {countLabel}
          </span>
        </div>
      </div>

      {abilities.map((ability, index) => (
        <div
          key={ability.label}
          ref={(node) => {
            boxRefs.current[index] = node;
          }}
          className={
            reduced
              ? `absolute ${STATIC_POSITIONS[index]} flex items-center gap-2 rounded-full border border-edge bg-surface/90 px-3.5 py-2.5 text-sm font-semibold text-ink shadow-lg shadow-black/10 backdrop-blur-md transition-transform duration-200 hover:scale-110 dark:shadow-black/40`
              : 'absolute left-1/2 top-1/2 flex will-change-transform items-center gap-2 rounded-full border border-edge bg-surface/90 px-3.5 py-2.5 text-sm font-semibold text-ink shadow-lg shadow-black/10 backdrop-blur-md transition-[transform,box-shadow] duration-200 hover:shadow-xl dark:shadow-black/40'
          }
          style={reduced ? undefined : { transform: 'translate(-50%, -50%)' }}
          onMouseEnter={() => {
            hoveredRef.current = index;
          }}
          onMouseLeave={() => {
            hoveredRef.current = -1;
          }}
        >
          <span className={`h-2.5 w-2.5 rounded-full ${ability.dot}`} aria-hidden />
          {ability.label}
        </div>
      ))}
    </div>
  );
}

export default AbilityOrbitVisual;
